import { useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import LeadHistory from "../../../../../components/organisms/LeadHistory/LeadHistory";
import { contentData } from "../../../../../utils/helpers/helperDataset/lead-management-contents/LeadDetailsDatset";
import { useAudioFile } from "../../../../../slices/leads/selector";
import { UserRole } from "../../../../../utils";
import ConversationSection from "./elements/ConversationSection";
import IconHome from "../../../../../styles/icons/IconHome";
import AIModal from "../../../../../components/templates/LeadTemplates/LeadModalsTemplate/AIModal";
import LoaderHome from "../../../../../components/atoms/Loader-home/Loader-home";

const LeadDetailsContent = ({ selectedLead }: { selectedLead: any }) => {
  const { instance } = useMsal();
  const activeAccount = instance?.getActiveAccount();
  const userRole = activeAccount?.idTokenClaims?.extension_ilpr as string;
  const audioFile = useAudioFile();

  const filteredContent = useMemo(() => {
    return userRole === UserRole.Operator
      ? contentData.filter((section) => section.name !== "Lead History")
      : contentData;
  }, [userRole]);

  const assignedUser = selectedLead?.assignedUsers?.[0];

  const customerName =
    assignedUser?.firstName || assignedUser?.lastName
      ? `${assignedUser?.firstName || ""} ${
          assignedUser?.lastName || ""
        }`.trim()
      : null;

  const companyName = assignedUser?.companyName || null;
  const jobName = assignedUser?.jobs?.[0]?.name || null;

  const getAudioSrc = (value: string) => {
    if (!value) return null;

    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("blob:") ||
      value.startsWith("data:")
    ) {
      return value;
    }

    return `data:audio/mpeg;base64,${value}`;
  };

  const getFieldValue = (key: string) => {
    switch (key) {
      case "isAudioFileDeleted": {
        const audioSrc = getAudioSrc(audioFile);
        if (!audioSrc) return <LoaderHome />;
        return (
          <audio key={`${selectedLead.id}-${audioSrc}`} controls className="w-100">
            <source src={audioSrc} />
            Your browser does not support the audio element.
          </audio>
        );
      }

      case "conversationSummary":
        return selectedLead?.conversationSummary || null;

      case "audioFileStringContent":
        return selectedLead?.conversationText ? (
          <ConversationSection conversation={selectedLead.conversationText} />
        ) : null;

      case "note":
        return selectedLead?.note || null;

      case "leadName": {
        const leadName = `${selectedLead?.firstName || ""} ${
          selectedLead?.lastName || ""
        }`.trim();
        return leadName || null;
      }

      case "address":
      case "addressOne": {
        const address = selectedLead?.[key];

        if (!address) return null;
        if (typeof address === "string") return address;

        return [
          address.addressLineOne,
          address.addressLineTwo,
          address.city,
          address.state,
          address.country,
        ]
          .filter(Boolean)
          .join(", ");
      }

      default: {
        const value = selectedLead?.[key];

        if (value === null || value === undefined || value === "") {
          return null;
        }

        if (typeof value === "object") {
          return Object.values(value).filter(Boolean).join(", ");
        }

        return value;
      }
    }
  };

  const renderOtherLeadDetails = () => {
    const createdDate = selectedLead?.createdAt;
    const leadId = selectedLead?.id;

    const rating = selectedLead?.rating;

    const company = selectedLead?.company;
    const businessType = selectedLead?.businessType;
    const revenue = selectedLead?.revenue;

    return (
      <div className="lead-details box-container">
        <div className="lead-details__header">
          <div className="lead-details__title">Lead Details</div>

          {rating && (
            <div>
              <span
                className={`chip chip--soft chip--status-${rating
                  .toLowerCase()
                  .replace(/\s+/g, "")}`}
              >
                {rating}
              </span>
            </div>
          )}
        </div>

        <div className="lead-details__grid">
          {leadId ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Lead Id</div>
              <div className="lead-details__value">{leadId}</div>
            </div>
          ) : null}

          {createdDate ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Created Date</div>
              <div className="lead-details__value">{createdDate}</div>
            </div>
          ) : null}

          {company ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Company</div>
              <div className="lead-details__value">{company}</div>
            </div>
          ) : null}

          {businessType ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Business Type</div>
              <div className="lead-details__value">{businessType}</div>
            </div>
          ) : null}

          {revenue ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Revenue</div>
              <div className="lead-details__value">{revenue}</div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const leadOwnerDetails = () => {
    const leadOwnerName =
      selectedLead?.assignedUsers[0]?.firstName +
      " " +
      selectedLead?.assignedUsers[0]?.lastName;

    const leadOwnerCompany = selectedLead?.assignedUsers[0]?.companyName;

    const job = selectedLead?.assignedUsers[0]?.jobs[0]?.name;

    return (
      <div className="lead-details box-container">
        <div className="lead-details__header">
          <div className="lead-details__title">Lead Owner Details</div>
        </div>

        <div className="lead-details__grid">
          {leadOwnerName ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Lead Owner name</div>
              <div className="lead-details__value">{leadOwnerName}</div>
            </div>
          ) : null}

          {leadOwnerCompany ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Lead Owner company</div>
              <div className="lead-details__value">{leadOwnerCompany}</div>
            </div>
          ) : null}

          {job ? (
            <div className="lead-details__field">
              <div className="lead-details__label">Job</div>
              <div className="lead-details__value">{job}</div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderLeadDetails = () => {
    const rating = selectedLead?.rating;
    const contactName = `${selectedLead?.firstName || ""} ${
      selectedLead?.lastName || ""
    }`.trim();
    const email = selectedLead?.email;
    const mobile = selectedLead?.mobile;
    const primaryAddress = getFieldValue("addressOne");

    return (
      <div className="lead-details-top box-container">
        <span className="lead-details__avatar-circle">
          {selectedLead?.firstName.charAt(0) + selectedLead?.lastName.charAt(0)}
        </span>
        <div>
          <div className="lead-details__primary">
            <h3>{contactName}</h3>
            {rating && (
              <div>
                <span
                  className={`chip chip--soft chip--status-${rating
                    .toLowerCase()
                    .replace(/\s+/g, "")}`}
                >
                  {rating}
                </span>
              </div>
            )}
          </div>

          <div className="lead-details__secondary">
            {email ? (
              <div className="d-flex gap-2 icon-small">
                <div className="lead-details__icon-wrapper">
                  <IconHome breadcrumb="emailIcon" />
                </div>

                <div className="lead-details__value">{email}</div>
              </div>
            ) : null}

            {mobile ? (
              <div className="d-flex gap-2 icon-small">
                <div className="lead-details__icon-wrapper">
                  <IconHome breadcrumb="leads" />
                </div>
                <div className="lead-details__value">{mobile}</div>
              </div>
            ) : null}

            {mobile ? (
              <div className="d-flex gap-2 icon-small">
                <div className="lead-details__icon-wrapper">
                  <IconHome breadcrumb="locationIcon" />
                </div>
                <div className="lead-details__value">{primaryAddress}</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderDefaultSection = (section: any) => {
    return (
      <div className={`${section.cssClass || ""} box-container lead-details`}>
        {section.showName && (
          <div className="lead-details__header border-0">
            <h2 className="lead-details__title">
              <IconHome breadcrumb={section.iconBreadcrumb || "summaryIcon"} />{" "}
              {section.name}
            </h2>
          </div>
        )}

        {section.name === "Lead History" ? (
          <div className="content-section-details">
            {LeadHistory(section.name, selectedLead)}
          </div>
        ) : (
          <div className="content-section-details">
            {section.fields?.map((field: any, fieldIndex: number) => {
              const value = getFieldValue(field.value);

              if (!value) return null;

              return (
                <div className={field.cssClass || ""} key={fieldIndex}>
                  <div className="details-area mb-3">
                    {field.name ? (
                      <div className={field.headerClass || "heading"}>
                        {field.name}
                      </div>
                    ) : null}

                    <div className={field.contentClass || "content w-100"}>
                      <div className="content-details w-100">{value}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const aiSection = () => {
    const aiResponse = selectedLead?.aiClassificationResponse;
    return (
      <div className="ai-section box-container">
        <div className="lead-details__header border-0 mt-2">
          <h2 className="lead-details__title">
            <IconHome breadcrumb="logoWithoutText" /> AI Verdict
          </h2>
        </div>

        <div className="content-section-details">
          <h3 className="text-md font-semibold d-flex gap-2 align-item-center">
            <IconHome breadcrumb="singleAiIcon" activeColor="#234cfc" />{" "}
            Interest level
          </h3>
          <span className="">{aiResponse?.reason.interestLevel}</span>

          <h3 className="text-md font-semibold d-flex gap-2 align-item-center">
            <IconHome breadcrumb="singleAiIcon" activeColor="#234cfc" />
            Decision making power
          </h3>
          <span className="">{aiResponse?.reason.decisionMakingPower}</span>

          <h3 className="text-md font-semibold d-flex gap-2 align-item-center">
            <IconHome breadcrumb="singleAiIcon" activeColor="#234cfc" /> Urgency
          </h3>
          <span className="">{aiResponse?.reason.urgency}</span>

          <h3 className="text-md font-semibold d-flex gap-2 align-item-center">
            <IconHome breadcrumb="singleAiIcon" activeColor="#234cfc" /> Client
            engagement
          </h3>
          <span className="">{aiResponse?.reason.engagement}</span>

          <h3 className="text-md font-semibold d-flex gap-2 align-item-center">
            <IconHome breadcrumb="singleAiIcon" activeColor="#234cfc" /> Erniee
            suggestion
          </h3>
          <span className="">{aiResponse?.suggestion}</span>
        </div>
      </div>
    );
  };

  const renderSection = (sectionName: string) => {
    const section = filteredContent.find((item) => item.name === sectionName);
    if (!section) return null;

    if (section.name === "Other Lead Details") {
      return renderOtherLeadDetails();
    }

    if (section.name === "Lead Details") {
      return renderLeadDetails();
    }

    if (section.name === "what erniee says") {
      return aiSection();
    }

    if (section.name === "Lead Owner details") {
      return leadOwnerDetails();
    }

    return renderDefaultSection(section);
  };

  return (
    <div className="lead-details-content">
      <Row>
        <Col md={12}>{renderSection("Lead Details")}</Col>
      </Row>

      <Row>
        <Col md={8}>
          {renderSection("what erniee says")}
          {renderSection("Other Lead Details")}
          {renderSection("Lead Owner details")}

          <Row className="equal-height-row">
            <Col md={12}>{renderSection("Lead History")}</Col>
          </Row>
        </Col>

        <Col md={4}>
          {renderSection("Conversation Summary")}
          {renderSection("Call Recording")}
          {renderSection("Internal Notes")}
          {renderSection("Call Transcript")}
        </Col>
      </Row>

      <Row>
        <Col md={12}></Col>
      </Row>
    </div>
  );
};

export default LeadDetailsContent;
